import React from 'react';
import {
  ArrowBendUpLeft, CaretDown, Chats, Check, CheckCircle, CheckSquare, Circle, Clock, EnvelopeSimple, FolderSimple, PaperPlaneTilt, Paperclip, PencilSimpleLine,
  Hourglass, Receipt, Square, Star, User, Users, WarningCircle, type Icon,
} from '@phosphor-icons/react';
import { BADGE_TON, CHIP_TON, type BadgeArt, type ZeilenBadge, type ZuordnungsChip } from '../../../../../components/immobilien/email/listenAnsicht';

const BADGE_ICON: Record<BadgeArt, Icon> = {
  erledigt: CheckCircle, offen: Circle, wiedervorlage: Clock, rueckmeldung: Hourglass, beantwortet: ArrowBendUpLeft, geplant: Clock, sendet: Clock, fehlgeschlagen: WarningCircle,
  person: User, team: Users, rechnung: Receipt, kipool: Receipt, duplikat: Receipt, abgelegt: FolderSimple, bearbeitung: PencilSimpleLine,
};

export interface KiKennzeichen {
  kategorie: { name: string; farbe: string | null } | null;
  notfall: boolean;
  dringend: boolean;
  kuerzel: string | null;
  absenderTyp: string | null;
  prozent: number | null;
  ticket: boolean;
}

export interface KonversationsZeile {
  id: string;
  name: string;
  ausgang: boolean;
  ungelesen: boolean;
  text: string;
  anhang: boolean;
  datum: string;
  aktiv: boolean;
  onClick: () => void;
  onKontext: (e: React.MouseEvent) => void;
}

export interface EmailZeileProps {
  id: string;
  name: string;
  initialen: string;
  betreff: string;
  vorschau: string;
  datum: string;
  ausgang: boolean;
  /** Absender fett/Punkt: irgendeine Mail der Gruppe ungelesen. */
  gruppeUngelesen: boolean;
  /** Betreff fett: diese Mail ungelesen. */
  ungelesen: boolean;
  hatAnhang: boolean;
  aktiv: boolean;
  gewaehlt: boolean;
  auswahlTitel: string;
  ziehend: boolean;
  badges: ZeilenBadge[];
  chips: ZuordnungsChip[];
  ki: KiKennzeichen | null;
  /** Erledigt-Haken nur bei eingehenden Mails. */
  erledigt: boolean | null;
  markiert: boolean;
  /** #527: Farbe des zuständigen Teams/Mitarbeiters (Kante links + leichte Tönung). */
  farbe?: { fg: string; bg: string } | null;
  konversation: { anzahl: number; offen: boolean; onUmschalten: () => void; zeilen: KonversationsZeile[] } | null;
  mobil: boolean;
  onOeffnen: () => void;
  onKontext: (e: React.MouseEvent) => void;
  onAuswahl: () => void;
  onErledigt: (e: React.MouseEvent) => void;
  onStern: (e: React.MouseEvent) => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
}

function Knopf({ titel, onClick, groesse, children }: { titel: string; onClick: (e: React.MouseEvent) => void; groesse: number; children: React.ReactNode }) {
  return (
    <button type="button" title={titel} aria-label={titel} onClick={e => { e.stopPropagation(); onClick(e); }}
      className="immo-hover-flaeche flex items-center justify-center cursor-pointer shrink-0"
      style={{ width: groesse, height: groesse, borderRadius: 10, border: 'none', background: 'transparent', margin: groesse > 30 ? '-8px -4px' : 0 }}>
      {children}
    </button>
  );
}

/** Eine Zeile der Liste (Screen 01/02): Glas-Karte mit Auswahl, Avatar, Kopf, Betreff + KI-Kennzeichen, Vorschau, Badges und Chips. */
export function EmailZeile(z: EmailZeileProps) {
  const kn = z.mobil ? 44 : 30;
  return (
    <>
      <div
        data-email-row
        data-email-id={z.id}
        role="button"
        tabIndex={0}
        aria-current={z.aktiv ? 'true' : undefined}
        draggable
        onDragStart={z.onDragStart}
        onDragEnd={z.onDragEnd}
        onClick={z.onOeffnen}
        onKeyDown={e => { if (e.key === 'Enter' && e.target === e.currentTarget) z.onOeffnen(); }}
        onContextMenu={z.onKontext}
        className={`flex cursor-pointer min-w-0 ${z.aktiv || z.gewaehlt ? '' : 'immo-glas'}`}
        style={{
          borderRadius: 20, padding: '12px 12px 12px 10px', gap: 10, outline: 'none',
          background: z.aktiv ? 'var(--acc-soft)' : z.gewaehlt ? 'var(--fill2)' : undefined,
          backgroundImage: !z.aktiv && !z.gewaehlt && z.farbe ? `linear-gradient(${z.farbe.bg}, ${z.farbe.bg})` : undefined,
          boxShadow: z.aktiv ? 'inset 0 0 0 2px var(--acc)' : z.gewaehlt ? 'inset 0 0 0 1px var(--line)' : z.farbe ? `inset 4px 0 0 ${z.farbe.fg}` : undefined,
          opacity: z.ziehend ? 0.45 : 1, transition: 'transform .15s, opacity .15s',
        }}
      >
        <div className="flex flex-col items-center shrink-0" style={{ gap: z.mobil ? 0 : 8 }}>
          <button type="button" title={z.auswahlTitel} aria-label={z.auswahlTitel} aria-pressed={z.gewaehlt}
            onClick={e => { e.stopPropagation(); z.onAuswahl(); }}
            className="flex items-center justify-center cursor-pointer"
            style={{ width: z.mobil ? 44 : 24, height: z.mobil ? 44 : 24, margin: z.mobil ? '-10px -10px 0' : 0, border: 'none', background: 'transparent', borderRadius: 8 }}>
            {z.gewaehlt ? <CheckSquare size={19} weight="fill" color="var(--acc)" /> : <Square size={19} color="var(--ink3)" />}
          </button>
          <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: z.gruppeUngelesen ? 'var(--info)' : 'transparent' }} />
        </div>

        <span aria-hidden className="flex items-center justify-center shrink-0"
          style={{ width: 38, height: 38, borderRadius: '50%', fontSize: 13, fontWeight: 800, background: 'var(--fill2)', color: 'var(--ink2)' }}>
          {z.initialen}
        </span>

        <div className="flex flex-col flex-1 min-w-0" style={{ gap: 4 }}>
          <div className="flex items-center min-w-0" style={{ gap: 6 }}>
            {z.ausgang && <PaperPlaneTilt size={13} color="var(--ink3)" className="shrink-0" />}
            <span className="flex-1 min-w-0 truncate" style={{ fontSize: 14, fontWeight: z.gruppeUngelesen ? 800 : 600 }}>{z.name}</span>
            {z.hatAnhang && <Paperclip size={14} color="var(--ink3)" aria-label="Hat Anhang" className="shrink-0" />}
            <span className="shrink-0" style={{ fontSize: 12, color: 'var(--ink3)', whiteSpace: 'nowrap' }}>{z.datum}</span>
            {z.erledigt !== null && (
              <Knopf titel={z.erledigt ? 'Als offen markieren' : 'Als erledigt markieren'} onClick={z.onErledigt} groesse={kn}>
                <CheckCircle size={19} weight={z.erledigt ? 'fill' : 'regular'} color={z.erledigt ? 'var(--ok)' : 'var(--ink3)'} />
              </Knopf>
            )}
            <Knopf titel={z.markiert ? 'Markierung entfernen' : 'Mit Stern markieren'} onClick={z.onStern} groesse={kn}>
              <Star size={19} weight={z.markiert ? 'fill' : 'regular'} color={z.markiert ? 'var(--warn)' : 'var(--ink3)'} />
            </Knopf>
          </div>

          <div className="flex items-center min-w-0" style={{ gap: 6 }}>
            <span className="min-w-0 truncate" style={{ fontSize: 13, fontWeight: z.ungelesen ? 700 : 500, color: z.ungelesen ? 'var(--ink)' : 'var(--ink2)' }}>{z.betreff}</span>
            {z.ki && <KiKennzeichenAnzeige ki={z.ki} />}
          </div>

          {z.vorschau && <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{z.vorschau}</span>}

          {(z.badges.length > 0 || z.chips.length > 0) && (
            <div className="flex flex-wrap" style={{ gap: 4 }}>
              {z.badges.map((b, i) => {
                const t = b.farbe ?? BADGE_TON[b.art];
                const I = BADGE_ICON[b.art];
                return (
                  <span key={`b${i}`} className="inline-flex items-center" style={{ height: 22, padding: '0 8px', borderRadius: 11, gap: 4, fontSize: 10, fontWeight: 800, whiteSpace: 'nowrap', background: t.bg, color: t.fg, maxWidth: '100%' }}>
                    <I size={11} weight="fill" className="shrink-0" /><span className="truncate">{b.label}</span>
                  </span>
                );
              })}
              {z.chips.map((c, i) => {
                const t = CHIP_TON[c.art];
                return (
                  <span key={`c${i}`} className="inline-flex items-center" style={{ height: 22, padding: '0 8px', borderRadius: 11, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap', background: t.bg, color: t.fg, maxWidth: '100%' }}>
                    <span className="truncate">{c.label}</span>
                  </span>
                );
              })}
            </div>
          )}

          {z.konversation && (
            <button type="button" onClick={e => { e.stopPropagation(); z.konversation?.onUmschalten(); }} aria-expanded={z.konversation.offen}
              title={`${z.konversation.anzahl} Nachrichten in dieser Konversation`}
              className="immo-hover-flaeche inline-flex items-center self-start cursor-pointer"
              style={{ height: z.mobil ? 44 : 26, padding: '0 10px', borderRadius: 13, gap: 5, border: 'none', fontSize: 11, fontWeight: 700, background: 'var(--fill2)', color: 'var(--ink2)' }}>
              <Chats size={13} />{z.konversation.anzahl} Nachrichten in dieser Konversation
              <CaretDown size={10} weight="bold" style={{ transform: z.konversation.offen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
            </button>
          )}
        </div>
      </div>

      {z.konversation?.offen && (
        <div className="flex flex-col" style={{ gap: 4, paddingLeft: z.mobil ? 20 : 44 }}>
          {z.konversation.zeilen.map(m => (
            <div key={m.id} role="button" tabIndex={0} onClick={m.onClick} onContextMenu={m.onKontext}
              onKeyDown={e => { if (e.key === 'Enter') m.onClick(); }}
              className="flex items-center cursor-pointer min-w-0"
              style={{ gap: 8, minHeight: z.mobil ? 44 : 36, padding: '0 10px', borderRadius: 13, background: 'var(--fill)', boxShadow: m.aktiv ? 'inset 0 0 0 2px var(--acc)' : 'inset 0 0 0 1px var(--line)' }}>
              {m.ausgang
                ? <PaperPlaneTilt size={13} color="var(--ink3)" className="shrink-0" />
                : <EnvelopeSimple size={13} color={m.ungelesen ? 'var(--info)' : 'var(--ink3)'} className="shrink-0" />}
              <span className="truncate shrink-0" style={{ fontSize: 12, fontWeight: m.ungelesen ? 800 : 600, maxWidth: '40%' }}>{m.name}</span>
              <span className="flex-1 min-w-0 truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{m.text}</span>
              {m.anhang && <Paperclip size={12} color="var(--ink3)" className="shrink-0" />}
              <span className="shrink-0" style={{ fontSize: 11, color: 'var(--ink3)', whiteSpace: 'nowrap' }}>{m.datum}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function KiKennzeichenAnzeige({ ki }: { ki: KiKennzeichen }) {
  return (
    <span className="flex items-center shrink-0" style={{ gap: 4 }}>
      {ki.kategorie && <span title={ki.kategorie.name} style={{ width: 8, height: 8, borderRadius: '50%', background: ki.kategorie.farbe || 'var(--ink3)' }} />}
      {ki.notfall && <span title="Notfall" style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--bad)', boxShadow: '0 0 0 3px var(--bad-soft)' }} />}
      {ki.dringend && <span className="flex items-center" style={{ height: 18, padding: '0 6px', borderRadius: 9, fontSize: 9, fontWeight: 800, letterSpacing: '0.04em', background: 'var(--bad-soft)', color: 'var(--bad)' }}>DRINGEND</span>}
      {ki.kuerzel && <span title={`Absender: ${ki.absenderTyp}`} className="flex items-center" style={{ height: 18, padding: '0 6px', borderRadius: 9, fontSize: 9, fontWeight: 800, background: 'var(--fill2)', color: 'var(--ink2)' }}>{ki.kuerzel}</span>}
      {ki.prozent !== null && <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink3)' }}>{ki.prozent}%</span>}
      {ki.ticket && <span title="Ticket vorhanden" className="flex"><Check size={12} weight="bold" color="var(--ok)" /></span>}
    </span>
  );
}
